import { ReactSVG } from 'react-svg'

import {
  useAuth,
  useLoginModal,
  useProfileCustomerModal,
} from '@features/auth'
import { useToggleFavorite } from '@features/favorite/hooks/useToggleFavorite'

import { Product } from '@shared/generated'
import { cn } from '@shared/lib/cn'
import Button, { ButtonComponentProps, type ButtonShapeVariant, type ButtonSizeVariant } from '@shared/ui/button'

import styles from './styles.module.scss'

interface FavoriteButtonProps extends Pick<Product, 'id'> {
  className?: string;
  size?: ButtonSizeVariant
  isFavorite?: boolean;
  theme?: ButtonComponentProps['theme'];
  appearance?: 'button' | 'square-button' | 'icon'
  shape?: ButtonShapeVariant
}

export default function FavoriteButton ({
  className,
  size,
  appearance = 'button',
  isFavorite = false,
  id,
  shape,
  theme = 'grey'
}: FavoriteButtonProps) {
  const { user, requireRegistration } = useAuth()
  const { openProfileCustomerModal } = useProfileCustomerModal()
  const { openLoginModal } = useLoginModal()
  const { toggleFavorite } = useToggleFavorite()

  const favoriteClickHandler = () => {
    if (!user?.id) {
      openLoginModal()
    }
    else if (requireRegistration) {
      openProfileCustomerModal()
    }
    else {
      toggleFavorite(id, isFavorite)
    }
  }

  const commonClassName = cn(styles.root, className, size ? styles[size] : '', {
    [styles.active]: isFavorite,
    [styles.asButton]: [ 'square-button', 'button' ].includes(appearance),
    [styles.large]: size === 'large',
  })

  return (
    <Button
      className={cn(commonClassName)}
      type="button"
      shape={shape}
      size={size}
      theme={theme}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        favoriteClickHandler()
      }}
    >
      <ReactSVG src="/icons/heart.svg" wrapper="span" />
    </Button>
  )
}
